/**
 * Containers Tab Component
 *
 * Main view for container management.
 * Includes filters, summary stats, and the container table.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ContainerTable } from './ContainerTable';
import { CreateContainerModal } from './CreateContainerModal';
import { AssignContainerModal } from './AssignContainerModal';
import { ReturnToYardModal } from './ReturnToYardModal';
import { useContainers } from '@/hooks/useContainers';
import { useStorageYards } from '@/hooks/useStorageYards';
import type { Container } from '@/services/resource-container.service';
import type { ContainerFilterDTO } from '@/schemas/containerSchema';

export function ContainersTab() {
  const [filters, setFilters] = useState<ContainerFilterDTO>({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [returnToYardModalOpen, setReturnToYardModalOpen] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);

  const { data: containerData, isLoading } = useContainers(filters);
  const { data: yardData } = useStorageYards();

  const containers = containerData?.containers || [];
  const yards = yardData?.yards || [];

  // Use summary statistics from API response
  const summary = containerData ? {
    total: containerData.total,
    byStatus: containerData.byStatus,
  } : null;

  const handleAssign = (container: Container) => {
    setSelectedContainer(container);
    setAssignModalOpen(true);
  };

  const handleRetrieve = (container: Container) => {
    // TODO: Implement retrieve modal
    console.log('Retrieve container:', container);
  };

  const handleReturnToYard = (container: Container) => {
    setSelectedContainer(container);
    setReturnToYardModalOpen(true);
  };

  const handleMaintenance = (container: Container) => {
    // TODO: Implement maintenance modal
    console.log('Mark for maintenance:', container);
  };

  const handleFilterChange = (
    key: keyof ContainerFilterDTO,
    value: string | boolean | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Available</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {summary.byStatus.available}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Deployed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {summary.byStatus.deployed}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">In Transit</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {summary.byStatus.in_transit}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Maintenance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {summary.byStatus.maintenance}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            {/* Yard Filter */}
            <select
              value={filters.yardId || ''}
              onChange={(e) => handleFilterChange('yardId', e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
            >
              <option value="">All Yards</option>
              {yards.map((yard) => (
                <option key={yard.id} value={yard.id}>
                  {yard.name}
                </option>
              ))}
              <option value="not_in_yard">Not in Yard</option>
            </select>

            {/* Size Filter */}
            <select
              value={filters.size || ''}
              onChange={(e) => handleFilterChange('size', e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
            >
              <option value="">All Sizes</option>
              <option value="10Y">10 Yard</option>
              <option value="20Y">20 Yard</option>
              <option value="30Y">30 Yard</option>
              <option value="40Y">40 Yard</option>
            </select>

            {/* Status Filter */}
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
            >
              <option value="">All Statuses</option>
              <option value="available">Available</option>
              <option value="deployed">Deployed</option>
              <option value="in_transit">In Transit</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>

            {/* Available Only Toggle */}
            <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer min-h-[44px]">
              <input
                type="checkbox"
                checked={filters.availableOnly || false}
                onChange={(e) => handleFilterChange('availableOnly', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-gray-700">Available Only</span>
            </label>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-gray-600 hover:text-gray-900"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Create Button */}
          <Button
            onClick={() => setCreateModalOpen(true)}
            size="lg"
            className="min-h-[44px] whitespace-nowrap"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Container
          </Button>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            {filters.yardId && (
              <Badge variant="secondary">
                Yard: {filters.yardId === 'not_in_yard'
                  ? 'Not in Yard'
                  : yards.find(y => y.id === filters.yardId)?.name || filters.yardId}
                <button
                  onClick={() => handleFilterChange('yardId', undefined)}
                  className="ml-2 hover:text-gray-900"
                >
                  ×
                </button>
              </Badge>
            )}
            {filters.size && (
              <Badge variant="secondary">
                Size: {filters.size}
                <button
                  onClick={() => handleFilterChange('size', undefined)}
                  className="ml-2 hover:text-gray-900"
                >
                  ×
                </button>
              </Badge>
            )}
            {filters.status && (
              <Badge variant="secondary">
                Status: {filters.status}
                <button
                  onClick={() => handleFilterChange('status', undefined)}
                  className="ml-2 hover:text-gray-900"
                >
                  ×
                </button>
              </Badge>
            )}
            {filters.availableOnly && (
              <Badge variant="secondary">
                Available Only
                <button
                  onClick={() => handleFilterChange('availableOnly', false)}
                  className="ml-2 hover:text-gray-900"
                >
                  ×
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Container Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <ContainerTable
          containers={containers}
          onAssign={handleAssign}
          onRetrieve={handleRetrieve}
          onReturnToYard={handleReturnToYard}
          onMaintenance={handleMaintenance}
          isLoading={isLoading}
        />
      </div>

      {/* Modals */}
      <CreateContainerModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />

      <AssignContainerModal
        open={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setSelectedContainer(null);
        }}
        container={selectedContainer}
      />

      <ReturnToYardModal
        open={returnToYardModalOpen}
        onClose={() => {
          setReturnToYardModalOpen(false);
          setSelectedContainer(null);
        }}
        container={selectedContainer}
      />
    </div>
  );
}
